import { ComponentProps } from 'react';
import { DateTimeInput } from './DateTimeInput';
import { useTimezoneStore } from '@/stores/timezoneStore';
import {
  convertUTCToTimezone,
  convertTimezoneToUTC,
  getTimezoneAbbreviation,
} from '@/utils/timezone';

interface TzDateTimeInputProps
  extends Omit<ComponentProps<typeof DateTimeInput>, 'value' | 'onChange' | 'type'> {
  /**
   * The UTC datetime value (ISO string like '2025-12-01T15:00:00.000Z')
   * Or undefined if no value set
   */
  value?: string;
  /**
   * Called when the datetime changes
   * Receives UTC ISO string (converted from user's timezone input)
   */
  onChange?: (utcValue: string) => void;
  /**
   * Type of date input: 'date' or 'datetime-local'
   * Default: 'datetime-local'
   */
  type?: 'date' | 'datetime-local';
}

/**
 * Timezone-aware DateTimeInput wrapper
 *
 * Handles timezone conversion automatically:
 * - Displays dates in user's selected timezone
 * - Converts user input back to UTC before calling onChange
 * - Updates display when timezone preference changes
 * - Shows "(Your Timezone)" in label instead of "(UTC)"
 *
 * Usage:
 * ```tsx
 * const [utcValue, setUtcValue] = useState('2025-12-01T15:00:00.000Z');
 *
 * <TzDateTimeInput
 *   label="Start Time"
 *   id="start_utc"
 *   value={utcValue}
 *   onChange={setUtcValue}
 * />
 * // User in America/New_York sees: Dec 1, 2025 10:00 AM
 * // When they edit it, onChange receives UTC ISO string
 * ```
 */
export function TzDateTimeInput({
  value,
  onChange,
  type = 'datetime-local',
  label,
  ...props
}: TzDateTimeInputProps) {
  const { timezone } = useTimezoneStore();

  // Convert UTC value to user's timezone for display
  const displayValue = value
    ? convertUTCToTimezone(value, timezone, type === 'date' ? 'date' : 'datetime-local')
    : '';

  // Handle input change: convert from user's timezone to UTC
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const localValue = e.target.value;

    if (!localValue) {
      onChange?.('');
      return;
    }

    // Convert from user's timezone to UTC
    const utcValue = convertTimezoneToUTC(localValue, timezone);
    onChange?.(utcValue);
  };

  // Get timezone abbreviation for label suffix
  const isDateTime = type === 'datetime-local';
  const timezoneSuffix = isDateTime ? `(${getTimezoneAbbreviation(timezone)})` : undefined;

  return (
    <DateTimeInput
      {...props}
      type={type}
      label={label}
      value={displayValue}
      onChange={handleChange}
      timezoneSuffix={timezoneSuffix}
      containerClassName={props.containerClassName}
    />
  );
}
